import { List, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useMapLayersState } from 'state/useMapLayersState';
import { MapLayers } from 'features/mapping/mapping.constants';
import { NgfsDetectionIcon, ThermalHotspotIcon } from 'features/mapping/icons';
import { LegendListItem } from './LegendListItem';
import { ThermalHotspotTimeGuide } from './ThermalHotspotTimeGuide';
import { FrpScaleGuide } from './FrpScaleGuide';
import { LegendSectionTitle } from './LegendSectionTitle';

export const SatelliteLegend = (): JSX.Element => {
  const { t } = useTranslation();
  const { mapLayers } = useMapLayersState();
  const isNgfsEnabled = useReleaseFlag('show_ngfs_detections_in_frontend');

  const showHeatDetections =
    mapLayers.includes(MapLayers.VIIRS) || mapLayers.includes(MapLayers.MODIS);
  const showNgfs =
    isNgfsEnabled && mapLayers.includes(MapLayers.NGFS_DETECTIONS);

  return (
    <List
      disablePadding
      dense
      subheader={
        <LegendSectionTitle>
          {t('map.legends.satellite.title')}
        </LegendSectionTitle>
      }
    >
      {showNgfs && (
        <LegendListItem
          iconSrc={NgfsDetectionIcon.data}
          iconSize={40}
          primaryText={t('map.legends.satellite.legends.ngfs.title')}
          secondary={
            <>
              <Typography variant="body2" component="span">
                <Trans
                  i18nKey="map.legends.satellite.legends.ngfs.body"
                  components={{ b: <strong /> }}
                />
              </Typography>
              <FrpScaleGuide />
            </>
          }
        />
      )}
      {showHeatDetections && (
        <>
          <LegendListItem
            iconSrc={ThermalHotspotIcon.data}
            iconSize={16}
            primaryText={t('map.legends.satellite.legends.viirs.title')}
            secondary={
              <>
                <Typography variant="body2">
                  {t('map.legends.satellite.legends.viirs.subtitle')}
                </Typography>
                <ThermalHotspotTimeGuide size="small" />
              </>
            }
          />
          <LegendListItem
            iconSrc={ThermalHotspotIcon.data}
            iconSize={26}
            primaryText={t('map.legends.satellite.legends.modis.title')}
            secondary={
              <>
                <Typography variant="body2" component="span">
                  {t('map.legends.satellite.legends.modis.subtitle')}
                </Typography>
                <ThermalHotspotTimeGuide size="small" />
              </>
            }
          />
        </>
      )}
    </List>
  );
};
